/**
 * appointmentHistoryRows.tsx
 *
 * The rows the Appointment History panel shows for each revision: the "Created"
 * snapshot and the "Edited" before -> after diff.
 *
 * Lives outside UnifiedAppointmentModal.tsx for the same reason as
 * appointmentHistoryActor.tsx: that file boots React on load through its store
 * imports, so nothing in it can be exercised on its own.
 */
import type React from 'react';
import { getTimezoneAbbreviation, utcToWallClock } from '@/lib/facilityTimezone';
import type {
  AppointmentHistoryEntry,
  AppointmentHistoryResource,
} from '@/services/appointments.service';

const PACIFIC_TZ = 'America/Los_Angeles';

function formatDateTimeDisplay(iso: string | null | undefined): string {
  if (!iso) return '';
  const clean = iso.replace(' ', 'T');
  const d = new Date(clean.endsWith('Z') ? clean : clean + 'Z');
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * Mirrors legacy `prepareText`/`prepareDate`/`prepareBoolean`
 * (`scheduler_appt_history.php` L169-L201): empty/blank values render as
 * "(Blank)" in italics, booleans render as "Checked" / "Not Checked",
 * dates fall back to "(Blank)" when zero-sentinel.
 */
function historyText(value: string | null | undefined): React.ReactNode {
  const trimmed = (value ?? '').trim();
  if (trimmed === '') {
    return <i className="text-slate-400">(Blank)</i>;
  }
  return trimmed;
}

export function historyDate(
  iso: string | null | undefined,
  facilityTz: string | null | undefined,
): React.ReactNode {
  if (!iso) {
    return <i className="text-slate-400">(Blank)</i>;
  }
  const normalized = (iso ?? '').replace(' ', 'T');
  const targetTz = facilityTz || PACIFIC_TZ;
  const wall = utcToWallClock(normalized, targetTz);
  if (!wall) return formatDateTimeDisplay(iso);
  const [y, mo, d] = wall.date.split('-').map(Number);
  const [h, mi] = wall.time.split(':').map(Number);
  const dateObj = new Date(y, mo - 1, d, h, mi);
  const formatted = dateObj.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
  const tzAbbr = getTimezoneAbbreviation(targetTz, dateObj);
  return `${formatted} ${tzAbbr}`;
}

function historyBoolean(flag: boolean): React.ReactNode {
  return flag ? 'Checked' : 'Not Checked';
}

function historyResourceList(rows: AppointmentHistoryResource[]): string {
  if (rows.length === 0) {
    return '';
  }
  return rows
    .map((r) => (r.resourceName && r.resourceName.trim() !== '' ? r.resourceName : `#${r.resourceId}`))
    .join(', ');
}

function historyPatientName(entry: AppointmentHistoryEntry): React.ReactNode {
  if (entry.patientId === 0) {
    return <i className="text-slate-400">Patient</i>;
  }
  const name = (entry.patientName ?? '').trim();
  if (name === '') {
    return <i className="text-slate-400">(Blank)</i>;
  }
  return name;
}

function historyGroupName(entry: AppointmentHistoryEntry): React.ReactNode {
  if (entry.appointmentTypeGroupId === 0) {
    return <i className="text-slate-400">(Blank)</i>;
  }
  const name = (entry.appointmentTypeGroupName ?? '').trim();
  return name === '' ? <i className="text-slate-400">(Blank)</i> : name;
}

function historyTypeName(entry: AppointmentHistoryEntry): React.ReactNode {
  if (entry.appointmentTypeId === 0) {
    return <i className="text-slate-400">(Blank)</i>;
  }
  const name = (entry.appointmentTypeName ?? '').trim();
  return name === '' ? <i className="text-slate-400">(Blank)</i> : name;
}

function historyFacilityName(entry: AppointmentHistoryEntry): React.ReactNode {
  if (entry.facilityId === 0) {
    return <i className="text-slate-400">(Blank)</i>;
  }
  const name = (entry.facilityName ?? '').trim();
  return name === '' ? <i className="text-slate-400">(Blank)</i> : name;
}

export interface HistoryDiffRow {
  label: string;
  before: React.ReactNode;
  after: React.ReactNode;
}

export interface HistorySnapshotRow {
  label: string;
  value: React.ReactNode;
}

/**
 * Builds the snapshot rows shown for the very first revision (creation).
 * Mirrors legacy `scheduler_appt_history.php` L249-L281: Start Date,
 * End Date, Name, Type, Facility, Notes, Contact Attempted, Confirmed,
 * Check In/Back/Out, Cancelled, No Show, Wait List, plus Resources Added
 * if any are attached. Order matches legacy, with Group (not on the legacy
 * page) placed ahead of Type.
 */
export function buildSnapshotRows(entry: AppointmentHistoryEntry): HistorySnapshotRow[] {
  const tz = entry.facilityIanaTimezone;
  const rows: HistorySnapshotRow[] = [
    { label: 'Start Date', value: historyDate(entry.startTime, tz) },
    { label: 'End Date', value: historyDate(entry.endTime, tz) },
    { label: 'Name', value: historyText(entry.name) },
    { label: 'Group', value: historyGroupName(entry) },
    { label: 'Type', value: historyTypeName(entry) },
    { label: 'Facility', value: historyFacilityName(entry) },
    { label: 'Notes', value: historyText(entry.reason) },
    { label: 'Contact Attempted', value: historyDate(entry.contactAttemptedAt, tz) },
    { label: 'Confirmed', value: historyDate(entry.confirmedDate, tz) },
    { label: 'Check In', value: historyDate(entry.checkInAt, tz) },
    { label: 'Check Back', value: historyDate(entry.checkBackAt, tz) },
    { label: 'Check Out', value: historyDate(entry.checkOutAt, tz) },
    { label: 'Cancelled', value: historyDate(entry.cancelledAt, tz) },
    { label: 'No Show', value: historyBoolean(entry.noShow) },
    { label: 'Wait List', value: historyBoolean(entry.waitList) },
  ];
  const resourceText = historyResourceList(entry.resources);
  if (resourceText !== '') {
    rows.push({ label: 'Resources Added', value: resourceText });
  }
  return rows;
}

/**
 * Builds the diff rows shown for an edit revision. Mirrors legacy
 * `scheduler_appt_history.php` L287-L380: every column the user changed
 * gets one row with [before] → [after]. Resources Added/Removed columns
 * compare the resource ID arrays between revisions.
 */
export function buildDiffRows(
  current: AppointmentHistoryEntry,
  previous: AppointmentHistoryEntry,
): HistoryDiffRow[] {
  const rows: HistoryDiffRow[] = [];
  const cTz = current.facilityIanaTimezone;
  const pTz = previous.facilityIanaTimezone;

  if (current.patientId !== previous.patientId) {
    rows.push({
      label: 'Patient',
      before: historyPatientName(previous),
      after: historyPatientName(current),
    });
  }
  if (current.patientPlanId !== previous.patientPlanId) {
    rows.push({
      label: 'PlanID',
      before: previous.patientPlanId,
      after: current.patientPlanId,
    });
  }
  if ((current.startTime ?? '') !== (previous.startTime ?? '')) {
    rows.push({
      label: 'Start Date',
      before: historyDate(previous.startTime, pTz),
      after: historyDate(current.startTime, cTz),
    });
  }
  if ((current.endTime ?? '') !== (previous.endTime ?? '')) {
    rows.push({
      label: 'End Date',
      before: historyDate(previous.endTime, pTz),
      after: historyDate(current.endTime, cTz),
    });
  }
  if ((current.duration ?? null) !== (previous.duration ?? null)) {
    rows.push({
      label: 'Duration',
      before: previous.duration === null ? <i className="text-slate-400">(Blank)</i> : `${previous.duration} minutes`,
      after: current.duration === null ? <i className="text-slate-400">(Blank)</i> : `${current.duration} minutes`,
    });
  }
  if (current.name !== previous.name) {
    rows.push({ label: 'Name', before: historyText(previous.name), after: historyText(current.name) });
  }
  // The group is re-derived from the type on every save, so this also fires when
  // a type was moved to another group and the appointment was then re-saved.
  if (current.appointmentTypeGroupId !== previous.appointmentTypeGroupId) {
    rows.push({
      label: 'Group',
      before: historyGroupName(previous),
      after: historyGroupName(current),
    });
  }
  if (current.appointmentTypeId !== previous.appointmentTypeId) {
    rows.push({
      label: 'Type',
      before: historyTypeName(previous),
      after: historyTypeName(current),
    });
  }
  if (current.facilityId !== previous.facilityId) {
    rows.push({
      label: 'Facility',
      before: historyFacilityName(previous),
      after: historyFacilityName(current),
    });
  }
  if (current.reason !== previous.reason) {
    rows.push({
      label: 'Notes',
      before: historyText(previous.reason),
      after: historyText(current.reason),
    });
  }
  if ((current.confirmedDate ?? '') !== (previous.confirmedDate ?? '')) {
    rows.push({
      label: 'Confirmed',
      before: historyDate(previous.confirmedDate, pTz),
      after: historyDate(current.confirmedDate, cTz),
    });
  }
  if ((current.checkInAt ?? '') !== (previous.checkInAt ?? '')) {
    rows.push({
      label: 'Check In',
      before: historyDate(previous.checkInAt, pTz),
      after: historyDate(current.checkInAt, cTz),
    });
  }
  if ((current.checkBackAt ?? '') !== (previous.checkBackAt ?? '')) {
    rows.push({
      label: 'Check Back',
      before: historyDate(previous.checkBackAt, pTz),
      after: historyDate(current.checkBackAt, cTz),
    });
  }
  if ((current.checkOutAt ?? '') !== (previous.checkOutAt ?? '')) {
    rows.push({
      label: 'Check Out',
      before: historyDate(previous.checkOutAt, pTz),
      after: historyDate(current.checkOutAt, cTz),
    });
  }
  if ((current.cancelledAt ?? '') !== (previous.cancelledAt ?? '')) {
    rows.push({
      label: 'Cancelled',
      before: historyDate(previous.cancelledAt, pTz),
      after: historyDate(current.cancelledAt, cTz),
    });
  }
  if ((current.contactAttemptedAt ?? '') !== (previous.contactAttemptedAt ?? '')) {
    rows.push({
      label: 'Contact Attempted',
      before: historyDate(previous.contactAttemptedAt, pTz),
      after: historyDate(current.contactAttemptedAt, cTz),
    });
  }
  if (current.noShow !== previous.noShow) {
    rows.push({
      label: 'No Show',
      before: historyBoolean(previous.noShow),
      after: historyBoolean(current.noShow),
    });
  }
  if (current.waitList !== previous.waitList) {
    rows.push({
      label: 'Wait List',
      before: historyBoolean(previous.waitList),
      after: historyBoolean(current.waitList),
    });
  }

  // Resources delta: legacy walks the previous and current ID arrays and
  // partitions into "added" / "removed" sets.
  const prevIds = new Set(previous.resources.map((r) => r.resourceId));
  const currIds = new Set(current.resources.map((r) => r.resourceId));
  const added = current.resources.filter((r) => !prevIds.has(r.resourceId));
  const removed = previous.resources.filter((r) => !currIds.has(r.resourceId));
  if (added.length > 0) {
    rows.push({ label: 'Resources Added', before: '', after: historyResourceList(added) });
  }
  if (removed.length > 0) {
    rows.push({ label: 'Resources Removed', before: historyResourceList(removed), after: '' });
  }

  return rows;
}
